import { myFetch } from '@commons/fetch';
import { useParams } from 'react-router';
import { useIntl } from 'react-intl';
import { notifications } from '@mantine/notifications';
import {
  Container,
  Flex,
  Title,
  Paper,
  Stack,
  Text,
  Loader,
  Center,
  Checkbox,
  ScrollArea,
  Button,
  TextInput,
  Box,
  Grid,
  ActionIcon,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { FilterField } from './types/FilterField';
import { useEffect, useState } from 'react';
import { IconGripVertical, IconX } from '@tabler/icons-react';
import { useDebouncedValue } from '@mantine/hooks';

interface FilterFormProps {
  filterId?: string;
  title: string;
  submitButtonText: string;
  onSubmit: (data: any) => Promise<void>;
}

export const FilterForm = ({
  filterId,
  title,
  submitButtonText,
  onSubmit,
}: FilterFormProps) => {
  const { username } = useParams();
  const intl = useIntl();

  const [availableFields, setAvailableFields] = useState<FilterField[]>([]);
  const [isLoadingFields, setIsLoadingFields] = useState(false);
  const [isLoading, setIsLoading] = useState(!!filterId);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(!!filterId);

  const form: any = useForm({
    mode: 'controlled',
    initialValues: {
      name: '',
      category: '',
      fields: [] as FilterField[],
    },

    validate: {
      name: (value: string) => {
        if (!value) {
          return intl.formatMessage({ id: 'required' });
        }
        return null;
      },
      category: (value: string) => {
        if (!value) {
          return intl.formatMessage({ id: 'required' });
        }
        return null;
      },
    },
  });

  const [debouncedCategory] = useDebouncedValue(form.values.category, 500);

  const handleSubmit = async (data: any) => {
    try {
      await onSubmit(data);
    } catch (err: any) {
      notifications.show({
        title: intl.formatMessage({ id: 'error' }),
        message: err?.message || intl.formatMessage({ id: 'error' }),
        color: 'red',
      });
    }
  };

  const handleFieldToggle = (field: FilterField, isChecked: boolean) => {
    const currentFields = form.values.fields || [];
    if (isChecked) {
      // Add field if not already present, maintaining order
      if (!currentFields.some((f: FilterField) => f.name === field.name)) {
        form.setFieldValue('fields', [...currentFields, field]);
      }
    } else {
      // Remove field
      form.setFieldValue(
        'fields',
        currentFields.filter((f: FilterField) => f.name !== field.name)
      );
    }
  };

  // Get available (unselected) fields
  const getAvailableFields = (): FilterField[] => {
    const selectedFields = form.values.fields || [];
    const selectedNames = new Set(
      selectedFields.map((f: FilterField) => f.name)
    );

    return availableFields.filter((field) => !selectedNames.has(field.name));
  };

  // Get selected fields in their current order
  const getSelectedFields = (): FilterField[] => {
    return form.values.fields || [];
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedIndex === null || draggedIndex === index) return;

    const selectedFields = getSelectedFields();
    const draggedField = selectedFields[draggedIndex];

    const newSelectedFields = [...selectedFields];
    newSelectedFields.splice(draggedIndex, 1);
    newSelectedFields.splice(index, 0, draggedField);

    form.setFieldValue('fields', newSelectedFields);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Fetch filter data if editing
  useEffect(() => {
    if (!filterId) return;

    const fetchFilter = async () => {
      try {
        setIsLoading(true);
        const response = await myFetch(`/${username}/filters/${filterId}`);
        const filter = response.data;

        const fieldNames = filter.fields || [];

        form.setValues({
          name: filter.name || '',
          category: filter.category || '',
          fields: [], // Will be set after availableFields are loaded
        });

        // Fetch available fields if category is set
        if (filter.category) {
          const fieldsResponse = await myFetch(
            `/${username}/models/fields?category=${filter.category}`
          );
          const fetchedFields: FilterField[] = fieldsResponse.data || [];
          setAvailableFields(fetchedFields);

          // Convert field names to Field objects
          // If filter.fields are already objects, use them directly
          // Otherwise, map field names to Field objects
          let fieldsAsObjects: FilterField[] = [];
          if (fieldNames.length > 0 && typeof fieldNames[0] === 'object') {
            // Already objects with type, name, label
            fieldsAsObjects = fieldNames as FilterField[];
          } else {
            // Array of strings, need to map to Field objects
            fieldsAsObjects = fieldNames
              .map((fieldName: string) =>
                fetchedFields.find((f: FilterField) => f.name === fieldName)
              )
              .filter(
                (f: FilterField | undefined): f is FilterField =>
                  f !== undefined
              );
          }

          form.setFieldValue('fields', fieldsAsObjects);
        }
      } catch (error: any) {
        notifications.show({
          title: intl.formatMessage({ id: 'error' }),
          message: error.message,
          color: 'red',
        });
      } finally {
        setIsLoading(false);
        setIsInitialLoad(false);
      }
    };

    fetchFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterId]);

  // Fetch fields when debounced category changes
  useEffect(() => {
    // Skip if we're in the initial load phase (loading filter data)
    if (isInitialLoad) {
      return;
    }

    const fetchFields = async () => {
      const category = debouncedCategory || '';

      if (!category) {
        setAvailableFields([]);
        if (!filterId) {
          // Only clear fields if creating new filter
          form.setFieldValue('fields', []);
        }
        return;
      }

      try {
        setIsLoadingFields(true);
        const response = await myFetch(
          `/${username}/models/fields?category=${category}`
        );
        const fetchedFields = response.data || [];
        setAvailableFields(fetchedFields);

        // Clear fields that are no longer in available fields
        const currentFields = form.values.fields || [];
        const validFields = currentFields.filter((f: FilterField) =>
          fetchedFields.some((af: FilterField) => af.name === f.name)
        );
        form.setFieldValue('fields', validFields);
      } catch (error: any) {
        // notifications.show({
        //   title: intl.formatMessage({ id: 'error' }),
        //   message: error.message,
        //   color: 'red',
        // });
        setAvailableFields([]);
      } finally {
        setIsLoadingFields(false);
      }
    };

    fetchFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedCategory]);

  if (isLoading) {
    return (
      <Container my="md" size="xl">
        <Center>
          <Loader size="lg" />
        </Center>
      </Container>
    );
  }

  return (
    <Container my="md" size="xl">
      <Title order={4} mb="md">
        {title}
      </Title>

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Flex gap="md" direction="column">
          <TextInput
            withAsterisk
            name="name"
            label={intl.formatMessage({ id: 'name' })}
            placeholder={intl.formatMessage({ id: 'name' })}
            {...form.getInputProps('name')}
          />

          <TextInput
            withAsterisk
            name="category"
            label={intl.formatMessage({ id: 'category' })}
            placeholder={intl.formatMessage({ id: 'category' })}
            {...form.getInputProps('category')}
          />

          {isLoadingFields && (
            <Center p="md">
              <Loader size="sm" />
            </Center>
          )}

          {!isLoadingFields && availableFields.length > 0 && (
            <Grid>
              <Grid.Col span={6}>
                <Paper p="md" radius="md" withBorder>
                  <Text size="sm" fw={500} mb="sm">
                    {intl.formatMessage({ id: 'availableFields' })} (
                    {getAvailableFields().length})
                  </Text>
                  <ScrollArea h={300}>
                    <Stack gap="xs">
                      {getAvailableFields().map((field) => {
                        return (
                          <Box
                            key={field.name}
                            p="xs"
                            style={{
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                            }}
                          >
                            <Checkbox
                              checked={false}
                              onChange={(event) =>
                                handleFieldToggle(
                                  field,
                                  event.currentTarget.checked
                                )
                              }
                              label={
                                <div>
                                  <Text size="sm" fw={500}>
                                    {field.label}
                                  </Text>
                                  <Text size="xs" c="dimmed">
                                    {field.name} ({field.type})
                                  </Text>
                                </div>
                              }
                            />
                          </Box>
                        );
                      })}
                    </Stack>
                  </ScrollArea>
                </Paper>
              </Grid.Col>
              <Grid.Col span={6}>
                <Paper p="md" radius="md" withBorder>
                  <Text size="sm" fw={500} mb="sm">
                    {intl.formatMessage({ id: 'selectedFields' })} (
                    {getSelectedFields().length})
                  </Text>
                  <ScrollArea h={300}>
                    <Stack gap="xs">
                      {getSelectedFields().map((field, index) => {
                        const isDragging = draggedIndex === index;

                        return (
                          <Box
                            key={field.name}
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragEnd={handleDragEnd}
                            p="xs"
                            style={{
                              borderRadius: '4px',
                              backgroundColor: isDragging
                                ? 'var(--mantine-color-blue-1)'
                                : 'transparent',
                              cursor: 'grab',
                              transition: 'background-color 0.2s',
                              border: isDragging
                                ? '2px dashed var(--mantine-color-blue-5)'
                                : '2px solid transparent',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                            }}
                            onMouseEnter={(e) => {
                              if (!isDragging) {
                                e.currentTarget.style.backgroundColor =
                                  'var(--mantine-color-gray-1)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isDragging) {
                                e.currentTarget.style.backgroundColor =
                                  'transparent';
                              }
                            }}
                          >
                            <IconGripVertical
                              size={20}
                              color="var(--mantine-color-gray-6)"
                              style={{ cursor: 'grab' }}
                            />
                            <Box style={{ flex: 1 }}>
                              <Text size="sm" fw={500}>
                                {field.label}
                              </Text>
                              <Text size="xs" c="dimmed">
                                {field.name} ({field.type})
                              </Text>
                            </Box>
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              onClick={() => handleFieldToggle(field, false)}
                              onDragStart={(e) => e.stopPropagation()}
                              draggable={false}
                            >
                              <IconX size={16} />
                            </ActionIcon>
                          </Box>
                        );
                      })}
                      {getSelectedFields().length === 0 && (
                        <Center p="md">
                          <Text size="sm" c="dimmed" ta="center">
                            {intl.formatMessage({ id: 'noFieldsSelected' })}
                          </Text>
                        </Center>
                      )}
                    </Stack>
                  </ScrollArea>
                </Paper>
              </Grid.Col>
            </Grid>
          )}

          {!isLoadingFields && availableFields.length === 0 && (
            <Paper p="md" radius="md" withBorder>
              <Text size="sm" c="dimmed" ta="center">
                {intl.formatMessage({ id: 'noFieldsAvailable' })}
              </Text>
            </Paper>
          )}

          <Button fullWidth type="submit" variant="primary">
            {submitButtonText}
          </Button>
        </Flex>
      </form>
    </Container>
  );
};
